import { Field, type FieldRootProps } from '@ark-ui/react'
import { classNames as cn } from '~/helpers/string'
import WarningIcon from '~/components/icons/Warning'

export interface TextareaProps extends Omit<FieldRootProps, 'invalid' | 'onChange'> {
  label: string
  name?: string
  error?: boolean
  helperText?: string
  errorText?: string
  placeholder?: string
  autoresize?: boolean
  cols?: number
  rows?: number
  value?: string
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>
}

export function Textarea(props: TextareaProps) {
  const {
    label,
    error,
    helperText,
    errorText,
    placeholder,
    autoresize,
    cols,
    rows,
    value,
    onChange,
    name,
    id,
    ...rest
  } = props

  return (
    <Field.Root className={cn('group relative w-full disabled:pointer-events-none')} invalid={error} {...rest}>
      <Field.Textarea
        id={id}
        name={name}
        className={cn(
          'border-1 peer block min-h-11 w-full rounded-md border-brand-neutral-200 bg-transparent p-3 pl-[14px] text-sm font-normal duration-100 invalid:border-2 invalid:border-brand-red-700 invalid:pr-9 hover:border-brand-neutral-500 focus:border-transparent focus:shadow-[0_0_0_2px_#671ddb] focus:outline-none focus:ring-0 invalid:focus:border-2 invalid:focus:border-brand-red-700 disabled:pointer-events-none disabled:text-brand-violet-950/50 disabled:opacity-60 disabled:hover:border-brand-neutral-200',
        )}
        placeholder={placeholder}
        autoresize={autoresize}
        cols={cols}
        rows={rows}
        value={value}
        onChange={onChange}
      />
      <Field.Label
        className={cn(
          'z-2 absolute left-3 top-2 -translate-y-4 transform bg-white px-1 text-xs text-brand-gray-500 duration-100 peer-focus:text-brand-purple-700 peer-disabled:pointer-events-none peer-disabled:text-brand-violet-950 peer-disabled:text-opacity-35 group-data-[invalid]:text-brand-red-700 group-data-[invalid]:focus:outline-brand-red-700',
        )}
      >
        {label}
        <Field.RequiredIndicator />
      </Field.Label>
      <Field.HelperText
        className={cn(
          'ml-4 mt-1 text-xs font-normal text-brand-gray-500 empty:hidden peer-disabled:pointer-events-none peer-disabled:text-brand-violet-950/35 group-data-[invalid]:hidden',
        )}
      >
        {helperText}
      </Field.HelperText>
      <Field.ErrorText
        className={cn(
          'ml-4 mt-[5px] hidden text-xs font-normal text-brand-red-700 peer-disabled:pointer-events-none peer-disabled:text-brand-violet-950/35 group-data-[invalid]:block group-data-[invalid]:empty:hidden',
        )}
      >
        {errorText}
      </Field.ErrorText>
      <WarningIcon
        width="20"
        height="20"
        className="gr absolute right-3 top-[23px] hidden -translate-y-1/2 transform text-brand-red-700 group-data-[invalid]:inline-block"
      />
    </Field.Root>
  )
}
